import { useEffect } from 'react'
import { useEntityPicture } from '../hooks/useEntityPicture'
import { IconTile, type IconTileSize } from './ui/icon-tile'
import { cn } from '../lib/utils'

export interface OrgLogoProps {
  org: string
  /** Whether the organization has a picture set (derived from `membership.picture`). */
  hasPicture: boolean
  /** Rendered size in pixels (width & height). Defaults to 24. */
  size?: number
  /** Organization name, used as the `<img>` alt text. */
  name?: string | null
  className?: string
  'data-testid'?: string
}

/**
 * Small, read-only rounded-SQUARE logo for chrome (the sidebar brand). Unlike
 * `EntityPicture` — built for the settings-page uploader, with a dashed drop
 * zone, a fixed 48px fallback icon and a click-to-zoom modal — this has no
 * upload / drag-drop / modal logic at all, mirroring `UserAvatar`'s shape
 * (same `useEntityPicture` hook, same "just show it or a muted placeholder"
 * contract) but with square corners, since an organization logo is a brand
 * mark, not a person's face.
 *
 * The no-picture fallback is an `IconTile` carrying the organisation's
 * initial (spec 2026-09-17-03) — a grey `Building` glyph said nothing the
 * name beside it did not, and read as a missing image.
 */
export function OrgLogo({
  org,
  hasPicture,
  size = 24,
  name,
  className,
  'data-testid': testId,
}: OrgLogoProps) {
  const { state, loadPicture } = useEntityPicture('organization', org, org, {
    enabled: hasPicture,
  })

  useEffect(() => {
    if (hasPicture) {
      loadPicture()
    }
  }, [hasPicture, loadPicture])

  if (!hasPicture || !state.imageUrl) {
    return (
      <IconTile
        initial={(name || org).trim().charAt(0) || '?'}
        gradient="neutral"
        tone="soft"
        size={nearestTileSize(size)}
        className={className}
        data-testid={testId}
      />
    )
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted text-muted-foreground',
        className,
      )}
      style={{ width: size, height: size }}
      data-testid={testId}
    >
      <img
        src={state.imageUrl}
        alt={name || ''}
        className="h-full w-full object-cover"
      />
    </span>
  )
}

/** `size` is a free pixel number here; the tile scale is four steps. */
function nearestTileSize(px: number): IconTileSize {
  if (px <= 27) return 'sm'
  if (px <= 36) return 'md'
  if (px <= 48) return 'lg'
  return 'xl'
}
